import { fetchAuthSession } from 'aws-amplify/auth';
import type { Adventure, AdventureIndex, Entry, HTMLSettings, UserIdentity } from '../types';

const API_BASE = 'https://rsh29b46ga.execute-api.us-east-1.amazonaws.com/adventure_metadata_handler';
const INIT_URL = 'https://rsh29b46ga.execute-api.us-east-1.amazonaws.com/initialize_user';
const TRANSFER_URL = 'https://rsh29b46ga.execute-api.us-east-1.amazonaws.com/transfer';
const CF_DOMAIN = 'https://dgsb783fzq4xo.cloudfront.net';

// ── Core request helpers ──────────────────────────────────

async function getToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}

function getStoredAccessKey(): string | null {
  try {
    const raw = localStorage.getItem('ae_user');
    if (!raw) return null;
    const identity = JSON.parse(raw);
    return identity?.access_key ?? null;
  } catch { return null; }
}

async function callAPI<T = Record<string, unknown>>(
  action: string,
  params: Record<string, unknown> = {},
): Promise<{ ok: boolean; data: T }> {
  try {
    const token = await getToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const body: Record<string, unknown> = { action, ...params };
    if (token) {
      headers['Authorization'] = token;
    } else {
      const ak = getStoredAccessKey();
      if (ak) body['access_key'] = ak;
    }
    const res = await fetch(API_BASE, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    const json = await res.json();
    if (json.statusCode !== undefined) {
      const body = typeof json.body === 'string' ? JSON.parse(json.body) : json.body;
      return { ok: json.statusCode === 200, data: body as T };
    }
    return { ok: res.ok, data: json as T };
  } catch (err) {
    console.error(`API error (${action}):`, err);
    return { ok: false, data: {} as T };
  }
}

async function callPublic<T>(url: string, payload: Record<string, unknown>): Promise<T | null> {
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const json = await res.json();
    if (json.statusCode !== undefined && json.body) {
      const body = typeof json.body === 'string' ? JSON.parse(json.body) : json.body;
      return json.statusCode === 200 ? (body as T) : null;
    }
    return json as T;
  } catch {
    return null;
  }
}

// ── User init ─────────────────────────────────────────────

export async function initializeUser(deviceUserId: string, email?: string): Promise<UserIdentity | null> {
  const data = await callPublic<any>(INIT_URL, {
    device_user_id: deviceUserId,
    ...(email ? { email } : {}),
    use_permanent_credentials: true,
  });
  if (!data) return null;
  return {
    user_id: data.userId,
    s3_prefix: data.s3Prefix,
    access_key: data.accessKey,
    is_pro_user: data.isProUser,
    aws_access_key_id: data.awsAccessKeyId,
    aws_secret_access_key: data.awsSecretAccessKey,
  };
}

// ── Metadata ──────────────────────────────────────────────


// ============================================================
// Add this function to your lib/api.ts file
// ============================================================


export async function updateTripGroups(
  tripGroups: { name: string; adventure_ids: string[]; published?: boolean }[]
): Promise<boolean> {
  const { ok } = await callAPI('updateTripGroups', { trip_groups: tripGroups });
  return ok;
}

export async function getMetadata() {
  const { ok, data } = await callAPI<AdventureIndex>('getMetadata');
  return ok ? data : null;
}

// ── Adventures ────────────────────────────────────────────

export const addAdventure = (description: string, comment: string, alias?: string) =>
  callAPI('addAdventure', { description, comment, alias: alias ?? '' }).then(r => r.ok);

export const updateAdventure = (id: string, description: string, comment: string, alias?: string) =>
  callAPI('updateAdventure', { adventure_id: id, description, comment, alias: alias ?? '' }).then(r => r.ok);

export const deleteAdventure = (id: string) =>
  callAPI('deleteAdventure', { adventure_id: id }).then(r => r.ok);

export const reorderAdventures = (orderMap: Record<string, number>) =>
  callAPI('reorderAdventures', { order_map: orderMap }).then(r => r.ok);

// ── Entries ───────────────────────────────────────────────

export const updateEntries = (adventureId: string, entries: Entry[], reorder = false) =>
  callAPI('updateEntries', {
    adventure_id: adventureId,
    entries: entries.map(e => ({
      day: e.day,
      header: e.header,
      description: e.description,
      photos: e.photos,
      ...(e.lat != null ? { lat: e.lat } : {}),
      ...(e.lng != null ? { lng: e.lng } : {}),
      ...(e.city ? { city: e.city } : {}),
      ...(e.country ? { country: e.country } : {}),
    })),
    reorder,
  }).then(r => r.ok);

// ── Location extraction ───────────────────────────────────

export interface LocationResult {
  lat: number;
  lng: number;
  city: string;
  country: string;
}

export async function extractLocation(
  adventureTitle: string,
  header: string,
  description: string,
): Promise<LocationResult | null> {
  const { ok, data } = await callAPI<LocationResult>('extractLocation', {
    adventure_title: adventureTitle,
    header,
    description,
  });
  if (!ok || !data.lat || !data.lng) return null;
  return data;
}

// ── HTML ──────────────────────────────────────────────────

export const generateHTML = () =>
  callAPI('generateStaticHTML').then(r => r.ok);

export async function checkHTMLExists(s3Prefix: string): Promise<boolean> {
  try {
    const res = await fetch(`${CF_DOMAIN}/${s3Prefix}index.html`);
    return res.ok;
  } catch {
    return false;
  }
}

// ── Settings ──────────────────────────────────────────────

export const updateFontScale = (scale: number) =>
  callAPI('updateFontScale', { font_scale: scale }).then(r => r.ok);

export const updateHTMLSettings = (settings: HTMLSettings) =>
  callAPI('updateHTMLSettings', { html_settings: settings }).then(r => r.ok);

export const updateProStatus = (isPro: boolean) =>
  callAPI('setProUser', { is_pro_user: isPro }).then(r => r.ok);

export const setSubdomain = (subdomain: string) =>
  callAPI<{ success: boolean; subdomain: string; url: string }>('setSubdomain', { subdomain }).then(r => r);

// ── Transfer codes ────────────────────────────────────────

export const createTransferCode = (code: string, expiresAt: string) =>
  callAPI('createTransferCode', { transfer_code: code.replace(/-/g, ''), expires_at: expiresAt }).then(r => r.ok);

export async function redeemTransferCode(code: string): Promise<UserIdentity | null> {
  const data = await callPublic<any>(TRANSFER_URL, {
    action: 'redeemTransferCode',
    transfer_code: code.toUpperCase().replace(/-/g, ''),
  });
  if (!data?.success) return null;
  return {
    user_id: data.userId,
    s3_prefix: data.s3Prefix,
    access_key: data.accessKey,
    is_pro_user: data.isProUser,
    aws_access_key_id: data.awsAccessKeyId,
    aws_secret_access_key: data.awsSecretAccessKey,
  };
}

// ── Photo upload ──────────────────────────────────────────

export async function getUploadURL(s3Key: string, contentType = 'image/jpeg') {
  const { ok, data } = await callAPI<{ uploadUrl: string; key: string }>('getUploadURL', {
    s3_key: s3Key,
    content_type: contentType,
  });
  return ok ? data : null;
}

/** Result carries the actual failure reason (S3's XML error body, or the
 * thrown network error) instead of a bare boolean -- silent upload
 * failures with zero feedback were the whole reason this got added. */
export async function uploadPhoto(uploadUrl: string, file: File | Blob): Promise<{ ok: boolean; error?: string }> {
  try {
    const contentType = file instanceof File ? (file.type || 'image/jpeg') : 'image/jpeg';
    const res = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file });
    if (res.ok) return { ok: true };
    const body = await res.text().catch(() => '');
    return { ok: false, error: `HTTP ${res.status}${body ? `: ${body.slice(0, 200)}` : ''}` };
  } catch (err) {
    // A CORS-blocked or network-level failure never reaches a response at
    // all -- fetch throws a generic TypeError with no server detail, but
    // it's still worth surfacing over nothing.
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
}

/** S3 key for thumbnail (in day subfolder) */
export function buildPhotoS3Key(s3Prefix: string, adventureId: string, day: string, filename: string) {
  return `${s3Prefix}${adventureId}/${String(parseInt(day) || 0).padStart(3, '0')}/${filename}`;
}

/** S3 key for full-size image (in adventure root folder) */
export function buildFullSizeS3Key(s3Prefix: string, adventureId: string, filename: string) {
  return `${s3Prefix}${adventureId}/${filename}`;
}

/** Thumbnail URL (day subfolder) */
export function photoURL(s3Prefix: string, adventureId: string, day: string, filename: string) {
  const d = String(parseInt(day) || 0).padStart(3, '0');
  return `${CF_DOMAIN}/${s3Prefix}${adventureId}/${d}/${encodeURIComponent(filename)}`;
}

/** Full-size URL (adventure root) */
export function fullPhotoURL(s3Prefix: string, adventureId: string, filename: string) {
  return `${CF_DOMAIN}/${s3Prefix}${adventureId}/${encodeURIComponent(filename)}`;
}

export function indexURL(s3Prefix: string) {
  return `${CF_DOMAIN}/${s3Prefix}index.html`;
}

// ── Image resizing ────────────────────────────────────────

const THUMBNAIL_MAX_DIMENSION = 800;
const THUMBNAIL_QUALITY = 0.7;

/**
 * Create a thumbnail from a File using Canvas.
 * Resizes to fit within THUMBNAIL_MAX_DIMENSION, compresses to JPEG.
 * Returns a Blob ready for upload.
 */
export function createThumbnail(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      let { width, height } = img;
      // Scale down if larger than max dimension
      if (width > THUMBNAIL_MAX_DIMENSION || height > THUMBNAIL_MAX_DIMENSION) {
        if (width > height) {
          height = Math.round(height * (THUMBNAIL_MAX_DIMENSION / width));
          width = THUMBNAIL_MAX_DIMENSION;
        } else {
          width = Math.round(width * (THUMBNAIL_MAX_DIMENSION / height));
          height = THUMBNAIL_MAX_DIMENSION;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('Canvas context failed'));
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        blob => {
          if (blob) {
            console.log(`Thumbnail created: ${width}x${height}, ${(blob.size / 1024).toFixed(1)}KB`);
            resolve(blob);
          } else {
            reject(new Error('toBlob returned null'));
          }
        },
        'image/jpeg',
        THUMBNAIL_QUALITY,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Image load failed'));
    };
    img.src = objectUrl;
  });
}

/**
 * Upload a photo at both full-size and thumbnail resolution.
 * - Full size → {prefix}{aid}/{filename}
 * - Thumbnail → {prefix}{aid}/{day}/{filename}
 * Returns true if both uploads succeed.
 */
export async function uploadPhotoDual(
  s3Prefix: string,
  adventureId: string,
  day: string,
  filename: string,
  file: File,
): Promise<{ ok: boolean; error?: string }> {
  console.log(`📸 uploadPhotoDual: ${filename} (${(file.size / 1024 / 1024).toFixed(1)}MB, ${file.type})`);

  // 1. Upload full-size to adventure root
  const fullKey = buildFullSizeS3Key(s3Prefix, adventureId, filename);
  console.log(`  → Full-size key: ${fullKey}`);
  const fullUrlData = await getUploadURL(fullKey, file.type || 'image/jpeg');
  if (!fullUrlData?.uploadUrl) {
    console.error('  ❌ Failed to get full-size upload URL');
    return { ok: false, error: 'Could not get an upload URL (storage limit reached, or a server error)' };
  }
  const fullResult = await uploadPhoto(fullUrlData.uploadUrl, file);
  if (!fullResult.ok) {
    console.error('  ❌ Failed to upload full-size photo:', fullResult.error);
    return { ok: false, error: fullResult.error || 'Upload failed' };
  }
  console.log('  ✅ Full-size uploaded');

  // 2. Create thumbnail and upload to day subfolder
  // Every failure branch below used to swallow the error and fall through
  // to `return { ok: true }` at the bottom, since only the full-size
  // upload's success was actually checked. That's what the photo grid
  // reads from (see api.photoURL -- the day-subfolder/thumbnail path, not
  // the full-size one), so a thumbnail-only failure meant the filename
  // got recorded as a successful upload and added to the entry's photos
  // list with no alert and nothing to retry -- just a permanently broken
  // image tile with no indication anything had gone wrong. Real case:
  // "IMG_1393.jpeg" showed a broken-image placeholder in the grid with no
  // failure ever reported for it. Report failure here too now, so it
  // flows through the same retry mechanism as a full-size failure.
  try {
    console.log('  → Creating thumbnail...');
    const thumbBlob = await createThumbnail(file);
    console.log(`  → Thumbnail blob: ${(thumbBlob.size / 1024).toFixed(1)}KB, type=${thumbBlob.type}`);
    const thumbKey = buildPhotoS3Key(s3Prefix, adventureId, day, filename);
    console.log(`  → Thumbnail key: ${thumbKey}`);
    const thumbUrlData = await getUploadURL(thumbKey, 'image/jpeg');
    if (!thumbUrlData?.uploadUrl) {
      console.error('  ❌ Failed to get thumbnail upload URL');
      return { ok: false, error: 'Full-size photo uploaded, but could not get a thumbnail upload URL' };
    }
    const thumbResult = await uploadPhoto(thumbUrlData.uploadUrl, thumbBlob);
    if (!thumbResult.ok) {
      console.error('  ❌ Thumbnail upload PUT failed:', thumbResult.error);
      return { ok: false, error: `Full-size photo uploaded, but thumbnail failed: ${thumbResult.error}` };
    }
    console.log('  ✅ Thumbnail uploaded');
  } catch (err) {
    console.error('  ❌ Thumbnail creation failed:', err);
    const msg = err instanceof Error ? err.message : String(err);
    return { ok: false, error: `Full-size photo uploaded, but thumbnail creation failed: ${msg}` };
  }

  return { ok: true };
}
